import React, { useEffect, useMemo } from 'react';
import Card from '@common/components/card';
import Icon from '@common/components/icon';
import { FormIcon } from '@modules/forms/components/form-icon';
import BlocksBar from '@modules/performance/components/block-bar/block-bar';
import { useDrawerStore } from './drawer-store';
import { useAppSelector } from '@common/hooks';
import { selected } from '@modules/organisation/selectors/organisation';
import { resolveScaleColorForValue } from '@modules/performance/utils/utils';
import Spinner from '@common/components/spinner';
import EvaluationDetailView from './evaluation-detail-view';
import { useRequest } from '@common/services/api';
import {
  EvaluationsMeta,
  EvaluationsResponse,
} from '@modules/performance/types';
import { PureList } from '@common/components/list';
import { Button } from '@common/components/button';
import { useTranslation } from 'react-i18next';
import { EPerformanceEvaluationStatus } from '@common/definitions';
import StatusChip from '@modules/performance/components/status-chip/status-chip';
import Placeholder from '@common/components/placeholder';

interface EvaluationTabProps {
  currentEvaluationId?: string;
}

const EvaluationTab = ({ currentEvaluationId }: EvaluationTabProps) => {
  const { t } = useTranslation();
  const organisation = useAppSelector(selected);
  const {
    drawerEvaluation,
    handleFetchDrawerEvaluation,
    currentEvaluationId: drawerCurrentId,
    initializeDrawer,
    onCloseDrawer,
  } = useDrawerStore();

  const [showDetailView, setShowDetailView] = React.useState(false);

  const apiConfig = useMemo(
    () => ({
      useCursor: true,
      limit: 10,
    }),
    []
  );

  const reportsEndpoint = useMemo(() => {
    if (drawerEvaluation?.data?.reviewee?.id) {
      return `/v1/organisations/${organisation.id}/users/${drawerEvaluation.data.reviewee.id}/performance/evaluations`;
    }
    return null;
  }, [drawerEvaluation?.data?.reviewee?.id, organisation.id]);

  const shouldFetchReports = !!reportsEndpoint;

  const stableEndpoint = reportsEndpoint ?? '';

  const {
    data: drawerEvaluationReports,
    loading: drawerLoadingReports,
    meta: metaData,
    refetch: refetchDrawerEvaluationReports,
    reset: resetDrawerEvaluationReports,
  } = useRequest<EvaluationsResponse>(stableEndpoint, apiConfig);

  const meta: EvaluationsMeta = metaData as unknown as EvaluationsMeta;

  useEffect(() => {
    if (currentEvaluationId && !drawerCurrentId) {
      initializeDrawer(currentEvaluationId);
    }
  }, [currentEvaluationId, drawerCurrentId, initializeDrawer]);

  const fetchedForRevieweeRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (shouldFetchReports && reportsEndpoint) {
      const currentRevieweeId = drawerEvaluation?.data?.reviewee?.id;
      if (
        currentRevieweeId &&
        fetchedForRevieweeRef.current !== currentRevieweeId
      ) {
        fetchedForRevieweeRef.current = currentRevieweeId;
        resetDrawerEvaluationReports();
      }
    }
  }, [
    reportsEndpoint,
    resetDrawerEvaluationReports,
    shouldFetchReports,
    drawerEvaluation?.data?.reviewee?.id,
  ]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getScore = (evaluation: any) => {
    const revieweeScore = evaluation.reviewers[0]?.competency_avg_score || 0;
    return revieweeScore;
  };

  const handleEvaluationClick = async (evaluationId: string) => {
    try {
      setShowDetailView(true);
      if (onCloseDrawer) {
        onCloseDrawer();
      }
      await handleFetchDrawerEvaluation(evaluationId);
    } catch (error) {
      console.error('Error fetching evaluation details:', error);
    }
  };

  const handleBackToList = () => {
    setShowDetailView(false);
  };
  const filteredEvaluations = React.useMemo(() => {
    if (!currentEvaluationId) return drawerEvaluationReports || [];
    return (drawerEvaluationReports || []).filter(
      (evaluation: any) => evaluation.id !== currentEvaluationId
    );
  }, [drawerEvaluationReports, currentEvaluationId]);



  const pagination = useMemo(() => meta.pagination, [meta.pagination]);

  if (showDetailView && drawerEvaluation) {
    return <EvaluationDetailView onBack={handleBackToList} />;
  }

  const renderEvaluationRow = ({ item: evaluation }: { item: any }) => {
    const score = getScore(evaluation);
    const color = resolveScaleColorForValue(evaluation.rating_scales || [], score, '#2563EB');
    const totalScales = evaluation.rating_scales?.length || 5;
    const completedDate = evaluation.completed_at;
    const startedDate = evaluation.created_at;

    return (
      <div
        key={evaluation.id}
        className="tw-cursor-pointer hover:tw-bg-gray-50 tw-transition-colors tw-duration-200"
        onClick={() => handleEvaluationClick(evaluation.id)}
      >
        <Card containerClassName="hover:tw-bg-gray-50 tw-transition-colors tw-duration-200">
          <Card.Content className="tw-flex tw-flex-col tw-p-5 tw-gap-4">
            <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
              <div className="tw-items-center tw-flex tw-flex-row tw-gap-3 tw-py-1 tw-w-full FormTitle">
                <FormIcon
                  form={evaluation.form}
                  iconClassName="!tw-flex-none !tw-mr-0"
                />
                <span className="tw-font-medium tw-text-gray-textDarkest tw-text-base">
                  {evaluation.form.title}
                </span>
              </div>
              <StatusChip
                status={evaluation.status as EPerformanceEvaluationStatus}
              />
              <Icon type="chevron_right" size={20} color="grey" />
            </div>

            <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
              <div className="tw-flex tw-flex-col tw-gap-1 tw-flex-start tw-w-1/2">
                <div className="tw-text-gray-muted tw-text-sm tw-font-medium tw-text-left">
                  Started on
                </div>
                <div className="tw-text-gray-textDarkest tw-text-base tw-font-medium">
                  {startedDate ? formatDate(startedDate) : '-'}
                </div>
              </div>
              <div className="tw-flex tw-flex-col tw-gap-1 tw-flex-start tw-w-1/2">
                <div className="tw-text-gray-muted tw-text-sm tw-font-medium tw-text-left">
                  Completed on
                </div>
                <div className="tw-text-gray-textDarkest tw-text-base tw-font-medium">
                  {completedDate ? formatDate(completedDate) : '-'}
                </div>
              </div>

              <div className="tw-flex tw-flex-col tw-gap-1 tw-w-1/2">
                <div className="tw-text-gray-muted tw-text-sm tw-font-medium tw-text-left">
                  Score
                </div>
                <div style={{ minWidth: '120px', minHeight: '20px' }}>
                  <BlocksBar
                    total={totalScales}
                    filled={score}
                    color={color}
                    isEmpty={score === null || score === undefined}
                  />
                </div>
              </div>
            </div>
          </Card.Content>
        </Card>
      </div>
    );
  };

  return (
    <PureList<any, {}>
      containerClassName="tw-p-1 tw-max-h-[calc(100vh-265px)] tw-h-[calc(100vh-265px)]
      tw-overflow-y-auto !tw-bg-background tw-flex tw-flex-col tw-gap-1"
      items={filteredEvaluations}
      renderRow={renderEvaluationRow}
      footer={
        <>
          {!!pagination?.next_cursor && (
            <Button
              className="tw-text-center tw-font-semibold tw-clear-both tw-border-solid
              tw-border-background tw-w-full tw-relative tw-cursor-pointer !tw-bg-white
              "
              onClick={refetchDrawerEvaluationReports}
              isLoading={drawerLoadingReports}
            >
              {t('chat:conversations_load_more')}
            </Button>
          )}
        </>
      }
      placeholder={
        drawerLoadingReports ? (
          <Spinner centered />
        ) : !shouldFetchReports ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-py-8">
            <Placeholder title={t('performance:no_evaluations')} innerClassName="!tw-bg-transparent" />
          </div>
        ) : (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-py-8">
            <Placeholder title={t('performance:no_evaluations')} innerClassName="!tw-bg-transparent" />
          </div>
        )
      }
    />
  );
};

export default EvaluationTab;
